import { ScopeOfPracticeEnum } from '@umss/shared/utils';
import {
  BackgroundCheckOutcomeEnum,
  BackgroundCheckStatusEnum
} from './background-check.model';
import { ContractorReviewSummaryDto } from './contractor/contractor-review/contractor-review-summary.dto';
import { DayOfWeek } from './days-of-week.model';
import { Location } from './location.model';
import { PagedRequestDto } from './paged-request.model';
import { User } from './user.model';

export interface Contractor {
  createdAt: string;
  updatedAt: string;
  id: string;
  userId: string;
  user?: Partial<User>;
  licenseNumber: string;
  licenseRenewalDate?: string;
  licenseState?: string;
  scopeOfPractice: ScopeOfPracticeEnum;
  scopeOfPracticeDisplay: string;
  status: ContractorStatus;
  statusDisplay: string;
  location?: Location;
  maxDriveTimeMinutes: number;
  profilePictureUrl: string;
  bio: string;
  yearsExperience?: number;
  preferredBasePayRate: number;
  urgentNeedsPayRate: number;
  isAvailable: boolean;
  isVerified: boolean;
  backgroundCheckCompleted: boolean;
  backgroundCheckDate?: string;
  backgroundCheckStatus?: BackgroundCheckStatusEnum;
  backgroundCheckOutcome?: BackgroundCheckOutcomeEnum | null;
  backgroundCheckProvider?: string | null;
  backgroundCheckDocumentUrl?: string | null;
  backgroundCheckReportUrl?: string | null;
  onboardingCompleted: boolean;
  averageRating?: number;
  reviewCount: number;
  totalShiftsCompleted: number;
  totalHoursWorked: number;
  distanceMiles?: number;
  searchRadiusMiles?: number;
  notifications?: boolean;
  shiftAlertMinutes?: number[];
  isFavorite: boolean;
  temporaryPassword?: string;
  ssn?: string;
  contactEmail?: string;
  isSearchRadiusDefault?: boolean;
  reviews?: ContractorReviewSummaryDto[];
  timeZone?: string;
}

export enum ContractorStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Suspended = 'Suspended',
  PendingVerification = 'PendingVerification',
  Rejected = 'Rejected'
}

export const CONTRACTOR_STATUS_DISPLAY: Record<ContractorStatus, string> = {
  [ContractorStatus.Active]: 'Active',
  [ContractorStatus.Inactive]: 'Inactive',
  [ContractorStatus.Suspended]: 'Suspended',
  [ContractorStatus.PendingVerification]: 'Pending Verification',
  [ContractorStatus.Rejected]: 'Rejected'
};

export const CONTRACTOR_STATUS_MESSAGE_DISPLAY: Record<
  ContractorStatus,
  string
> = {
  [ContractorStatus.Active]:
    'This contractor is active and available for shifts.',
  [ContractorStatus.Inactive]:
    'This contractor is inactive and not available for shifts.',
  [ContractorStatus.Suspended]:
    'This contractor is suspended. Please contact support for more information.',
  [ContractorStatus.PendingVerification]:
    'This contractor is pending verification. They will be available for shifts once verified.',
  [ContractorStatus.Rejected]:
    'This contractor has been rejected. Please contact support for more information.'
};

export interface ContractorRequest extends PagedRequestDto {
  ScopeOfPractice: string;
  IsAvailable: boolean;
  IsVerified: boolean;
  Latitude: number;
  Longitude: number;
  RadiusMiles: number;
  MinRating: number;
  MinYearsExperience: number;
}

export interface ContractorPayload {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  licenseNumber: string;
  licenseRenewalDate: string;
  scopeOfPractice: string;
  location: Partial<Location>;
  maxDriveTimeMinutes: number;
  bio: string;
  yearsExperience: number;
  preferredBasePayRate: number;
  urgentNeedsPayRate: number;
  dateOfBirth: string;
}

export interface ContractorAvailabilityDto {
  id: string;
  dayOfWeek: DayOfWeek;
  dayOfWeekDisplay?: string;
  startTime: string;
  endTime: string;
  isActive?: boolean;
}

export interface CreateContractorAvailability {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}

export interface UpdateContractorAvailability {
  dayOfWeek?: DayOfWeek;
  startTime?: string;
  endTime?: string;
  isActive?: boolean;
}

// Availability Exceptions

export interface ContractorAvailabilityException {
  id: number;
  exceptionDate: string;
  startTime: string | null;
  endTime: string | null;
  isUnavailable: boolean;
  reason: string | null;
}

export interface CreateContractorAvailabilityException {
  exceptionDate: string;
  isUnavailable: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

export interface UpdateContractorAvailabilityException {
  isUnavailable?: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
}

export interface ContractorScheduleDto {
  weeklyAvailability: ContractorAvailabilityDto[];
  exceptions: ContractorAvailabilityException[];
}

export interface AvailabilityExceptionParams {
  startDate: string;
  endDate: string;
}

export interface UpdateContractorDto {
  firstName: string;
  lastName: string;
  phone: string;
  contactEmail: string;
  ssn?: string;
  licenseNumber?: string;
  licenseRenewalDate?: string;
  licenseState?: string;
  location?: Location;
  maxDriveTimeMinutes?: number;
  bio?: string;
  yearsExperience?: number;
  preferredBasePayRate?: number;
  urgentNeedsPayRate?: number;
  isAvailable?: boolean;
  scopeOfPractice: ScopeOfPracticeEnum;
  status?: ContractorStatus;
  distanceMiles?: number;
  notifications?: boolean;
  shiftAlertMinutes?: number[];
}
